import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// ── Configurable fields ─────────────────────────────────────────────
// Update these values as needed — they are referenced throughout the page.
const EFFECTIVE_DATE = 'January 1, 2026'
const LAST_UPDATED = 'February 25, 2026'
const JURISDICTION = 'Texas'
const CONTACT_EMAIL = 'support@playsocrates.com'
const LEGAL_ENTITY = 'Bobblehead Sports, LLC'
// ─────────────────────────────────────────────────────────────────────

export default function Terms() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-stone-300">
      <div className="sticky top-0 z-50 pt-[env(safe-area-inset-top)] bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="py-3">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-300 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-12 max-w-3xl">
        <h1 className="text-3xl font-bold text-white mb-2 mt-4">Terms of Use</h1>
        <p className="text-stone-500 text-sm mb-2">Effective Date: {EFFECTIVE_DATE}</p>
        <p className="text-stone-500 text-sm mb-8">Last Updated: {LAST_UPDATED}</p>

        <p className="text-sm leading-relaxed mb-6">
          Welcome to Socrates ("Socrates," "we," "our," or "us"). These Terms of Use
          ("Terms") govern your access to and use of the Socrates mobile application,
          website, and related services (collectively, the "Service").
        </p>
        <p className="text-sm leading-relaxed mb-8">
          By creating an account or using Socrates, you agree to these Terms. If you do
          not agree, you may not use the Service.
        </p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">1. Eligibility</h2>
            <p>
              You must be at least 18 years old (or the age of majority in your
              jurisdiction) to use Socrates.
            </p>
            <p className="mt-2">By using the Service, you represent and warrant that:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>You meet the applicable age requirement.</li>
              <li>Your use complies with all local laws and regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">2. Nature of the Service</h2>
            <p>
              Socrates is a skill-based football competition platform that allows users to:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Create or join private leagues</li>
              <li>Select teams from real-world football competitions</li>
              <li>Earn points based on official match results</li>
              <li>View league standings</li>
            </ul>
            <p className="mt-2">
              Socrates does not operate as a sportsbook and does not process wagers or
              gambling transactions.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">3. Accounts and Authentication</h2>
            <p>
              To use Socrates, you must create an account using a valid email address.
              Authentication is conducted through secure email-based login links.
            </p>
            <p className="mt-2">You agree to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Provide accurate and complete information</li>
              <li>Use only your own email address</li>
              <li>Maintain control of your account access</li>
              <li>Notify us of any unauthorized use</li>
            </ul>
            <p className="mt-2">
              You are responsible for all activity that occurs under your account.
              We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4. Rules and Scoring</h2>
            <p>
              Points are awarded based on official match outcomes as outlined in the
              in-app Rules section.
            </p>
            <p className="mt-2">
              While we strive for accuracy, match data is sourced from third-party
              providers. We do not guarantee uninterrupted availability or absolute
              accuracy of external data feeds.
            </p>
            <p className="mt-2">Socrates reserves the right to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Correct scoring errors</li>
              <li>Adjust standings if necessary</li>
              <li>Modify rules prospectively</li>
              <li>Void results affected by system malfunction or abuse</li>
            </ul>
            <p className="mt-2">
              All scoring and competition decisions made by Socrates are final.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Use the Service for unlawful purposes</li>
              <li>Attempt to exploit bugs or manipulate scoring</li>
              <li>Create multiple accounts for unfair advantage</li>
              <li>Reverse engineer or interfere with the platform</li>
              <li>Upload malicious software or harmful code</li>
            </ul>
            <p className="mt-2">
              We may suspend or permanently terminate accounts engaged in abuse or
              manipulation.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">6. Intellectual Property</h2>
            <p>
              All content, software, design elements, trademarks, and logos within
              Socrates are owned by Socrates or its licensors and are protected by
              intellectual property laws.
            </p>
            <p className="mt-2">
              You are granted a limited, non-exclusive, non-transferable license to use
              the Service for personal, non-commercial purposes only.
            </p>
            <p className="mt-2">
              You may not copy, distribute, modify, or create derivative works without
              written permission.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">7. Third-Party Services</h2>
            <p>
              Socrates may rely on third-party providers for hosting, authentication,
              analytics, and sports data.
            </p>
            <p className="mt-2">
              We are not responsible for outages, inaccuracies, or interruptions caused
              by third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">8. Service Availability</h2>
            <p>
              The Service is provided on an "as is" and "as available" basis.
            </p>
            <p className="mt-2">We do not guarantee:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Continuous availability</li>
              <li>Error-free performance</li>
              <li>That the Service will meet your expectations</li>
            </ul>
            <p className="mt-2">
              We may update, modify, or discontinue features at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Socrates shall not be liable for:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Indirect or consequential damages</li>
              <li>Loss of data or profits</li>
              <li>Platform downtime</li>
              <li>Scoring discrepancies</li>
            </ul>
            <p className="mt-2">
              Our total liability, if any, shall not exceed the amount you paid to use the
              Service in the preceding 12 months (if applicable).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">10. Termination</h2>
            <p>
              We may suspend or terminate your access at any time if you violate these
              Terms or engage in conduct that harms the Service or its users.
            </p>
            <p className="mt-2">You may stop using Socrates at any time.</p>
            <p className="mt-2">
              Upon termination, your right to use the Service ceases immediately.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">11. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will revise the
              "Last Updated" date.
            </p>
            <p className="mt-2">
              Your continued use of the Service after updates constitutes acceptance of
              the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of {JURISDICTION}, without regard to
              conflict of law principles.
            </p>
            <p className="mt-2">
              Any disputes shall be resolved in the courts of {JURISDICTION}.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">13. Contact</h2>
            <p>For questions regarding these Terms, please contact:</p>
            <ul className="mt-2 space-y-1 text-stone-400">
              <li>Email: {CONTACT_EMAIL}</li>
              <li>Company: {LEGAL_ENTITY}</li>
            </ul>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-stone-700 text-center text-stone-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Socrates. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
